import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Image,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  FilePdfOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';

import { extractApiErrorMessage } from '@/api/client';
import { getSubmission } from '@/api/submissions';
import {
  ASSIGNMENT_TYPE_LABELS,
  SUBMISSION_STATUS_LABELS,
  type AssignmentType,
  type SubmissionStatus,
} from '@/types/assignments';

const { Title, Text } = Typography;

interface Props {
  submissionId: number | null;
  open: boolean;
  onClose: () => void;
}

const TYPE_COLOR: Record<AssignmentType, string> = {
  NORMAL: 'blue',
  WEEK: 'cyan',
  EXAM: 'volcano',
};

const STATUS_COLOR: Record<SubmissionStatus, string> = {
  SUBMITTED: 'gold',
  REVIEWED: 'blue',
  NEEDS_REVISION: 'orange',
  APPROVED: 'green',
  SUBMIT_LATER: 'red',
};

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : '—';
}

interface AssignmentQuestionOption {
  id: string;
  content: string;
}

interface AssignmentQuestion {
  id: string;
  content: string;
  isImportant?: boolean;
  isMultipleChoice?: boolean;
  allowMultipleAnswers?: boolean;
  options?: AssignmentQuestionOption[];
}

function isQuestionShape(obj: unknown): obj is AssignmentQuestion {
  if (!obj || typeof obj !== 'object') return false;
  const o = obj as Record<string, unknown>;
  return typeof o.id === 'string' && typeof o.content === 'string';
}

function tryParseQuestions(raw: string | null): AssignmentQuestion[] | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('[')) return null;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    if (!parsed.every(isQuestionShape)) return null;
    return parsed as AssignmentQuestion[];
  } catch {
    return null;
  }
}

interface SubmissionAnswerItem {
  id: string;
  content?: string;
  answer?: string;
}

interface ParsedAnswer {
  type?: string;
  selectedOptionIds?: string[];
  value?: string;
}

function isSubmissionAnswerItem(obj: unknown): obj is SubmissionAnswerItem {
  if (!obj || typeof obj !== 'object') return false;
  const o = obj as Record<string, unknown>;
  return typeof o.id === 'string';
}

function tryParseSubmissionContent(
  raw: string | null,
): SubmissionAnswerItem[] | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('[')) return null;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    if (!parsed.every(isSubmissionAnswerItem)) return null;
    return parsed as SubmissionAnswerItem[];
  } catch {
    return null;
  }
}

function tryParseAnswer(raw: string | undefined): ParsedAnswer | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed as ParsedAnswer;
    }
    if (typeof parsed === 'string') {
      return { value: parsed };
    }
    if (typeof parsed === 'number' || typeof parsed === 'boolean') {
      return { value: String(parsed) };
    }
  } catch {
    return { value: raw };
  }
  return { value: raw };
}

const HTML_TAG_REGEX = /<\/?[a-zA-Z][^>]*>/;

/**
 * The student-side editor emits a custom <video-player src="…"> element that
 * DOMPurify strips during sanitisation (unknown tag). Rewrite it to a real
 * <video controls> element so the player renders inline.
 */
const VIDEO_PLAYER_REGEX =
  /<video-player\b[^>]*?\bsrc=["']([^"']+)["'][^>]*?(?:\/>|>\s*<\/video-player>)/gi;

function rewriteCustomMediaTags(raw: string): string {
  return raw.replace(
    VIDEO_PLAYER_REGEX,
    (_match, src: string) =>
      `<video controls preload="metadata" src="${src}" ` +
      `style="max-width:100%;height:auto;border-radius:6px;display:block;"></video>`,
  );
}

function RichHtml({ html }: { html: string | null }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [previewState, setPreviewState] = useState<{
    visible: boolean;
    current: number;
  }>({ visible: false, current: 0 });

  const clean = useMemo(() => {
    if (!html || !HTML_TAG_REGEX.test(html)) return null;
    return DOMPurify.sanitize(rewriteCustomMediaTags(html), {
      // <video> and its standard attributes are already in DOMPurify's allow
      // list; keep ADD_ATTR explicit so newer DOMPurify defaults don't drop
      // playback-critical attributes.
      ADD_ATTR: ['controls', 'preload', 'playsinline', 'poster'],
    });
  }, [html]);

  // After the sanitized HTML is injected, find every <img>, collect its src
  // and wire a click-to-preview handler. Event delegation keeps things simple.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      setImageUrls([]);
      return;
    }
    const imgs = Array.from(container.querySelectorAll('img'));
    const urls = imgs.map((img) => img.currentSrc || img.src).filter(Boolean);
    setImageUrls(urls);

    imgs.forEach((img) => {
      img.style.cursor = 'zoom-in';
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.borderRadius = '6px';
    });

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target || target.tagName !== 'IMG') return;
      const idx = imgs.indexOf(target as HTMLImageElement);
      if (idx < 0) return;
      event.preventDefault();
      event.stopPropagation();
      setPreviewState({ visible: true, current: idx });
    };
    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [clean]);

  if (!html || !html.trim()) {
    return <Text type="secondary">—</Text>;
  }

  if (clean) {
    return (
      <>
        <div
          ref={containerRef}
          className="rich-html"
          style={{ wordBreak: 'break-word' }}
          dangerouslySetInnerHTML={{ __html: clean }}
        />
        {imageUrls.length > 0 ? (
          <div style={{ display: 'none' }}>
            <Image.PreviewGroup
              items={imageUrls}
              preview={{
                visible: previewState.visible,
                current: previewState.current,
                zIndex: 2000,
                onVisibleChange: (visible, _prev, current) =>
                  setPreviewState((prev) => ({
                    visible,
                    current: current ?? prev.current,
                  })),
              }}
            />
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
      {html.trim()}
    </div>
  );
}

function AnswerCard({
  item,
  question,
  index,
}: {
  item: SubmissionAnswerItem;
  question: AssignmentQuestion | undefined;
  index: number;
}) {
  const answer = tryParseAnswer(item.answer);
  const selectedIds = new Set(answer?.selectedOptionIds ?? []);
  const isMC =
    question?.isMultipleChoice === true || answer?.type === 'multiple-choice';
  const multi = question?.allowMultipleAnswers === true;
  const textValue = answer?.value ?? null;
  const hasMcOptions = isMC && (question?.options?.length ?? 0) > 0;

  return (
    <Card size="small" styles={{ body: { padding: 12 } }}>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Space size={6} wrap>
          <Text strong>Câu {index + 1}</Text>
          {isMC ? (
            <Tag color={multi ? 'purple' : 'blue'}>
              {multi ? 'Trắc nghiệm · nhiều đáp án' : 'Trắc nghiệm · 1 đáp án'}
            </Tag>
          ) : (
            <Tag>Tự luận</Tag>
          )}
        </Space>

        {question?.content ? (
          <div
            style={{
              padding: 8,
              background: 'var(--color-surface-alt, #fafafa)',
              borderRadius: 4,
              borderLeft: '3px solid var(--color-primary, #1677ff)',
            }}
          >
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              Đề bài
            </Text>
            <RichHtml html={question.content} />
          </div>
        ) : null}

        <div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
            Trả lời
          </Text>
          {hasMcOptions ? (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              {question!.options!.map((opt, oidx) => {
                const selected = selectedIds.has(opt.id);
                return (
                  <div
                    key={opt.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 6,
                      padding: '2px 6px',
                      borderRadius: 4,
                      background: selected
                        ? 'rgba(13, 148, 136, 0.08)'
                        : 'transparent',
                    }}
                  >
                    <Text style={{ color: selected ? 'var(--color-primary)' : undefined }}>
                      {selected ? '●' : multi ? '☐' : '○'}
                    </Text>
                    <Text strong>{String.fromCharCode(65 + oidx)}.</Text>
                    <div style={{ flex: 1 }}>
                      <RichHtml html={opt.content} />
                    </div>
                    {selected ? <Tag color="green">Đã chọn</Tag> : null}
                  </div>
                );
              })}
            </Space>
          ) : textValue ? (
            <RichHtml html={textValue} />
          ) : item.answer ? (
            <pre
              style={{
                margin: 0,
                padding: 8,
                background: 'var(--color-surface-alt)',
                borderRadius: 4,
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {item.answer}
            </pre>
          ) : (
            <Text type="secondary">Chưa trả lời</Text>
          )}
        </div>
      </Space>
    </Card>
  );
}

function AttachmentList({
  imageUrls,
  pdfUrls,
  videoUrls,
}: {
  imageUrls: string[];
  pdfUrls: string[];
  videoUrls: string[];
}) {
  const total = imageUrls.length + pdfUrls.length + videoUrls.length;
  if (total === 0) {
    return <Text type="secondary">Không có file đính kèm</Text>;
  }
  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      {imageUrls.length > 0 ? (
        <Image.PreviewGroup
          preview={{
            // Drawer defaults to z-index 1000; lift the preview above it so
            // the lightbox isn't trapped behind the open drawer panel.
            zIndex: 2000,
          }}
        >
          <Space size={6} wrap>
            {imageUrls.map((url) => (
              <Image
                key={url}
                src={url}
                alt="attachment"
                width={120}
                height={120}
                style={{
                  objectFit: 'cover',
                  borderRadius: 6,
                  border: '1px solid var(--color-border)',
                  cursor: 'zoom-in',
                }}
              />
            ))}
          </Space>
        </Image.PreviewGroup>
      ) : null}
      {pdfUrls.length > 0 ? (
        <Space direction="vertical" size={4}>
          {pdfUrls.map((url, idx) => (
            <a key={url} href={url} target="_blank" rel="noopener noreferrer">
              <FilePdfOutlined /> PDF #{idx + 1}
            </a>
          ))}
        </Space>
      ) : null}
      {videoUrls.length > 0 ? (
        <Space direction="vertical" size={4}>
          {videoUrls.map((url, idx) => (
            <a key={url} href={url} target="_blank" rel="noopener noreferrer">
              <VideoCameraOutlined /> Video #{idx + 1}
            </a>
          ))}
        </Space>
      ) : null}
    </Space>
  );
}

export function SubmissionDetailDrawer({ submissionId, open, onClose }: Props) {
  const detailQuery = useQuery({
    queryKey: ['submission', submissionId],
    queryFn: () => getSubmission(submissionId as number),
    enabled: open && submissionId !== null,
  });

  const detail = detailQuery.data;

  const parsedQuestions = useMemo(
    () => tryParseQuestions(detail?.assignmentDescription ?? null),
    [detail?.assignmentDescription],
  );

  const answerItems = useMemo(
    () => tryParseSubmissionContent(detail?.content ?? null),
    [detail?.content],
  );

  const questionById = useMemo(
    () => new Map(parsedQuestions?.map((q) => [q.id, q]) ?? []),
    [parsedQuestions],
  );

  const errorMessage = detailQuery.error
    ? extractApiErrorMessage(detailQuery.error, 'Không tải được thông tin bài làm.')
    : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="80%"
      styles={{ wrapper: { maxWidth: 1280 } }}
      destroyOnClose
      title={
        <Space direction="vertical" size={0}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Bài làm học viên
          </Text>
          <Title level={4} style={{ margin: 0 }}>
            {detail?.fullname ?? (detailQuery.isLoading ? 'Đang tải…' : '—')}
          </Title>
        </Space>
      }
    >
      {errorMessage ? (
        <Alert
          type="error"
          showIcon
          message="Lỗi"
          description={errorMessage}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {detailQuery.isLoading ? (
        <Skeleton paragraph={{ rows: 8 }} active />
      ) : detail ? (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card size="small" styles={{ body: { padding: 12 } }}>
            <Space size={12} align="start">
              <Avatar
                src={detail.avatarUrl ?? undefined}
                icon={<UserOutlined />}
                size={48}
              />
              <Space direction="vertical" size={2}>
                <Text strong style={{ fontSize: 16 }}>
                  {detail.fullname ?? '—'}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {detail.email ?? '—'}
                  {detail.mobile ? ` · ${detail.mobile}` : ''}
                </Text>
                <Space size={4} wrap>
                  {detail.status ? (
                    <Tag color={STATUS_COLOR[detail.status]}>
                      {SUBMISSION_STATUS_LABELS[detail.status]}
                    </Tag>
                  ) : null}
                  <Tag>Nộp lúc: {formatDateTime(detail.submittedAt)}</Tag>
                  {detail.feedbackCount > 0 ? (
                    <Tag color="geekblue">
                      {detail.feedbackCount} feedback
                    </Tag>
                  ) : null}
                </Space>
              </Space>
            </Space>
          </Card>

          <Descriptions
            column={1}
            size="small"
            bordered
            labelStyle={{ width: 150 }}
            title="Thông tin bài tập"
          >
            <Descriptions.Item label="Tên bài tập">
              <Space size={6} wrap>
                <Text strong>
                  {detail.assignmentTitle ?? `#${detail.assignmentId}`}
                </Text>
                {detail.assignmentType ? (
                  <Tag color={TYPE_COLOR[detail.assignmentType]}>
                    {ASSIGNMENT_TYPE_LABELS[detail.assignmentType]}
                  </Tag>
                ) : null}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Cộng đồng">
              {detail.communityName ?? '—'}
              {detail.communityId ? ` (ID #${detail.communityId})` : ''}
            </Descriptions.Item>
            <Descriptions.Item label="Hạn nộp">
              {formatDateTime(detail.assignmentDueDate)}
            </Descriptions.Item>
            <Descriptions.Item label="Cập nhật">
              {formatDateTime(detail.updatedAt)}
            </Descriptions.Item>
          </Descriptions>

          <div>
            <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
              Bài làm
            </Title>
            {answerItems ? (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {answerItems.map((item, idx) => (
                  <AnswerCard
                    key={item.id || idx}
                    item={item}
                    question={questionById.get(item.id)}
                    index={idx}
                  />
                ))}
              </Space>
            ) : detail.content ? (
              <Card size="small" styles={{ body: { padding: 12 } }}>
                <RichHtml html={detail.content} />
              </Card>
            ) : (
              <Empty description="Học viên chưa nộp nội dung" />
            )}
          </div>

          <div>
            <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
              File đính kèm
            </Title>
            <AttachmentList
              imageUrls={detail.imageUrls}
              pdfUrls={detail.pdfUrls}
              videoUrls={detail.videoUrls}
            />
          </div>
        </Space>
      ) : null}
    </Drawer>
  );
}
